import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { PriceInput } from '../../../shared/components/PriceInput';
import { CustomOrderStatusBadge } from './CustomOrderStatusBadge';
import { AssetReview } from './AssetReview';
import { useCustomOrder, useCustomOrderCheckout, useMarkReady, useSetPricing } from '../hooks/useCustomOrders';
import type { CustomFieldSnapshot } from '../api/customOrdersApi';

const symbolFor = (currency: string) => (currency === 'NGN' ? '₦' : currency === 'USD' ? '$' : '');

const formatMoney = (amountMinor: number | null | undefined, currency: string) => {
  if (amountMinor == null) return '—';
  return `${symbolFor(currency)}${(amountMinor / 100).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

/** Render an intake answer value (primitive / array / object) as readable text. */
function renderValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.map((v) => renderValue(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function labelFor(key: string, snapshot?: CustomFieldSnapshot[] | null): string {
  const def = snapshot?.find((d) => d.key === key);
  if (def?.label) return def.label;
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const sectionTitle = 'text-[12px] font-[700] uppercase tracking-wide text-muted-foreground mb-3';

interface Props {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  canManage: boolean;
}

export const CustomOrderDetailModal = ({ orderId, isOpen, onClose, canManage }: Props) => {
  const { data: order, isLoading, error } = useCustomOrder(isOpen ? orderId : null);
  const setPricing = useSetPricing();
  const markReady = useMarkReady();
  const checkout = useCustomOrderCheckout();

  const [priceMinor, setPriceMinor] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (order?.amountMinor != null) setPriceMinor(order.amountMinor);
  }, [order?.amountMinor]);

  // A link belongs to one order; drop it when the modal is pointed at another.
  useEffect(() => {
    setCheckoutUrl(null);
    setCopied(false);
  }, [orderId]);

  const isDraft = order?.status === 'DRAFT' || order?.status === 'MEDIA_PROCESSING';
  const hasPrice = (order?.amountMinor ?? 0) > 0 || priceMinor > 0;
  const assets = order?.assets ?? [];
  const intakeEntries = order?.intake ? Object.entries(order.intake) : [];

  const handleSavePrice = () => {
    if (!order || priceMinor <= 0) return;
    setPricing.mutate({ id: order.id, amountMinor: priceMinor, currency: order.currency });
  };

  const handleMarkReady = () => {
    if (!order) return;
    markReady.mutate(order.id);
  };

  const handleGetPaymentLink = () => {
    if (!order) return;
    checkout.mutate(order.id, { onSuccess: (res) => setCheckoutUrl(res.checkoutUrl) });
  };

  const handleCopyLink = () => {
    if (!checkoutUrl) return;
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-foreground">Custom order</h2>
              {order && <CustomOrderStatusBadge status={order.status} />}
            </div>
            {order && (
              <p className="text-[13px] text-muted-foreground mt-1">
                {order.customerEmail} · #{order.id.slice(0, 8)} · {formatDate(order.createdAt)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">
            &times;
          </button>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-muted-foreground">Loading order…</div>
        ) : error || !order ? (
          <div className="py-16 text-center text-red-500">Failed to load this order. Please try again.</div>
        ) : (
          <div className="flex flex-col gap-8">
            {/* Intake */}
            <section>
              <h3 className={sectionTitle}>Request details</h3>
              {intakeEntries.length === 0 ? (
                <p className="text-[14px] text-muted-foreground">No intake details.</p>
              ) : (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {intakeEntries.map(([key, value]) => (
                    <div key={key}>
                      <dt className="text-[12px] text-muted-foreground">{labelFor(key, order.fieldSchemaSnapshot)}</dt>
                      <dd className="text-[14px] text-foreground">{renderValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>

            {/* Consultation */}
            {order.bookingId && (
              <section>
                <h3 className={sectionTitle}>Consultation</h3>
                <p className="text-[14px] text-foreground">
                  A consultation is linked to this order
                  <span className="text-muted-foreground"> (booking #{order.bookingId.slice(0, 8)})</span>.
                </p>
              </section>
            )}

            {/* Assets */}
            <section>
              <h3 className={sectionTitle}>Assets ({assets.length})</h3>
              {assets.length === 0 ? (
                <p className="text-[14px] text-muted-foreground">No uploaded assets.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {assets.map((asset) => (
                    <AssetReview key={asset.id} asset={asset} />
                  ))}
                </div>
              )}
            </section>

            {/* Pricing + Mark ready */}
            <section>
              <h3 className={sectionTitle}>Pricing</h3>
              {isDraft && canManage ? (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-48">
                    <PriceInput label={`Price (${order.currency})`} value={priceMinor} onChange={setPriceMinor} />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSavePrice}
                    disabled={priceMinor <= 0 || setPricing.isPending}
                    loading={setPricing.isPending}
                  >
                    Save price
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleMarkReady}
                    disabled={!hasPrice || markReady.isPending}
                    loading={markReady.isPending}
                  >
                    Mark as ready
                  </Button>
                  {!hasPrice && (
                    <span className="text-[12px] text-muted-foreground self-center">Set a price to enable “Mark as ready”.</span>
                  )}
                </div>
              ) : (
                <p className="text-[14px] text-foreground">{formatMoney(order.amountMinor, order.currency)}</p>
              )}
            </section>

            {/* Payment link — only once the order is checkout-ready upstream (PE-2112) */}
            {order.status === 'AWAITING_PAYMENT' && canManage && (
              <section>
                <h3 className={sectionTitle}>Payment link</h3>
                {checkoutUrl ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <code className="flex-1 bg-muted rounded-md px-3 py-2 text-sm font-mono break-all">
                        {checkoutUrl}
                      </code>
                      <button
                        onClick={handleCopyLink}
                        className="px-3 py-2 border border-border rounded-md text-sm hover:bg-muted whitespace-nowrap"
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <a
                        href={checkoutUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-2 border border-border rounded-md text-sm hover:bg-muted whitespace-nowrap"
                      >
                        Open
                      </a>
                    </div>
                    <p className="text-[12px] text-muted-foreground">
                      Share this link with {order.customerEmail} to collect payment.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button size="sm" onClick={handleGetPaymentLink} loading={checkout.isPending}>
                      Get payment link
                    </Button>
                    <span className="text-[12px] text-muted-foreground">
                      Opens a hosted checkout you can send to the customer directly.
                    </span>
                  </div>
                )}
              </section>
            )}

            {/* History */}
            {order.history && order.history.length > 0 && (
              <section>
                <h3 className={sectionTitle}>History</h3>
                <ol className="flex flex-col gap-2">
                  {order.history.map((h) => (
                    <li key={h.id} className="flex items-center gap-3 text-[13px]">
                      <CustomOrderStatusBadge status={h.status} />
                      <span className="text-muted-foreground">{formatDate(h.createdAt)}</span>
                      {h.note && <span className="text-foreground">— {h.note}</span>}
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
